import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {Project} from '@app/modules/shared/models';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';
import {
  ACCESS_SETTINGS,
  AccessMethod,
  accessOf,
  expiresInDays,
  generateSecurityToken,
  legacySignAvailable,
  parseWidgetSettings,
  SignMode,
  tokenProblem
} from './share-widget-access.model';

/** What a tab builds its sample links and code from: its Security section's settings as they stand. */
export interface AccessSecurityState {
  /** The Token / Salt as a save would store it, or '' when there is none. */
  securityToken: string;
  signMode: SignMode;
  includeExpiry: boolean;
  /** Unix seconds 30 days ahead while Include expiry is checked, otherwise ''. */
  expires: number | '';
}

/** A method's saved settings, for its tab to start from before its section reports anything. */
export function savedAccessSecurityOf(
  project: Project | null,
  method: AccessMethod
): Pick<AccessSecurityState, 'securityToken' | 'signMode'> {
  const access = accessOf(parseWidgetSettings(project?.widget_settings) || {}, method);
  return {securityToken: access.securityToken ?? '', signMode: access.signMode};
}

interface SignModeOption {
  value: SignMode;
  label: string;
  description: string;
}

// Most secure first; Legacy Sign last, and only where it is still offered.
const SIGN_MODE_OPTIONS: SignModeOption[] = [
  {value: 'request_sign', label: 'Request Sign', description: 'A signature per link (most secure)'},
  {value: 'global_token', label: 'Global Token', description: 'The token itself in the link'},
  {value: 'none', label: 'None', description: 'No token'},
  {value: 'legacy_md5', label: 'Legacy Sign', description: 'md5(email + salt), for old integrations'}
];

const SIGN_MODE_WARNINGS: Record<AccessMethod, Partial<Record<SignMode, string>>> = {
  email: {
    none: 'Anyone with the link can open the share page without a token.',
    global_token: 'The token travels in the links; anyone who has it can open the share page for any email.',
    legacy_md5: 'LEGACY: md5 links never expire and are not tied to the project. Move your integration to Request Sign.'
  },
  join: {
    none: 'Anyone with the link can join without a token.',
    global_token: 'The token travels in the links; anyone who has it can join for any email.'
  }
};

const GLOBAL_TOKEN_EDIT_WARNINGS: Record<AccessMethod, string> = {
  email: 'WARNING: anyone who has the token can edit any influencer.',
  join: 'WARNING: anyone who has the token can update any existing contact.'
};

@Component({
  selector: 'app-share-widget-access-security',
  templateUrl: './share-widget-access-security.component.html',
  styleUrls: ['./share-widget-access-security.component.scss']
})
export class ShareWidgetAccessSecurityComponent implements OnChanges {
  @Input() project: Project;
  /** The tab's access method: the section reads this method's settings only. */
  @Input() method: AccessMethod;
  /** Every control stays disabled while this is on (the tab's access method is off). */
  @Input() locked = false;
  /** The tab's Save is under way. */
  @Input() saving = false;
  @Output() stateChange = new EventEmitter<AccessSecurityState>();

  /** The sign options this tab offers, set as it loads. */
  signModeOptions: SignModeOption[] = SIGN_MODE_OPTIONS.filter((option) => option.value !== 'legacy_md5');

  // The Token / Salt as shown, signed with and saved: one value, so a save can never write a
  // token other than the one on screen.
  tokenInput = '';
  signMode: SignMode = 'request_sign';
  globalTokenEdit = false;
  // A link-generation option, never saved.
  includeExpiry = true;
  // The token / salt on screen was generated because the field was empty, and is not saved yet.
  autoGenerated = false;

  private savedToken: string | null = null;
  private expires: number | '' = expiresInDays(30);

  constructor(private modal: SbModalService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.project || changes.method) && this.project) this.initializeSettings();
  }

  /** Off while saving or locked; each control adds its own rules on top. */
  get controlsDisabled(): boolean {
    return this.saving || this.locked;
  }

  /** The token as a save would write it: the saved one untouched, anything typed trimmed. */
  get tokenToSave(): string {
    return this.tokenInput === (this.savedToken ?? '') ? this.tokenInput : this.tokenInput.trim();
  }

  get tokenProblem(): string | null {
    return tokenProblem(this.tokenInput, this.savedToken);
  }

  get state(): AccessSecurityState {
    return {
      securityToken: this.tokenToSave.trim() ? this.tokenToSave : '',
      signMode: this.signMode,
      includeExpiry: this.includeExpiry,
      expires: this.includeExpiry ? this.expires : ''
    };
  }

  /** The field shows for the modes that use it, named for what it is in that mode. */
  get tokenVisible(): boolean {
    return this.signMode !== 'none';
  }

  get tokenLabel(): string {
    return this.signMode === 'global_token' ? 'Token' : 'Salt';
  }

  get autoGeneratedPending(): boolean {
    return this.autoGenerated && this.tokenVisible && this.tokenInput !== (this.savedToken ?? '');
  }

  /** The token field's own description and error lines. */
  get tokenDescription(): string {
    return this.autoGeneratedPending ? `A new ${this.tokenWord} was generated. Save to keep it.` : '';
  }

  /** A Global Token travels in every link; a salt only signs them, so it can stay on the server. */
  get tokenHelp(): string {
    return this.signMode === 'global_token'
      ? 'This tab\'s own token. Every link carries it, so anyone who has one link can make links for any email.'
      : `This tab's own ${this.tokenWord}. Keep it on your server: anyone who has it can make links that this tab accepts.`;
  }

  /** The field's name in running text. */
  private get tokenWord(): string {
    return this.tokenLabel.toLowerCase();
  }

  get tokenError(): string {
    return this.tokenProblem ? `${this.tokenProblem}. Generate makes one.` : '';
  }

  get signModeWarning(): string | null {
    return SIGN_MODE_WARNINGS[this.method]?.[this.signMode] || null;
  }

  /** Request Sign's own line, under the options: its links open the contact's page with edit access. */
  get signModeInfo(): string {
    return this.signMode === 'request_sign' ? 'Already existing influencers can also edit their info via the widget.' : '';
  }

  get globalTokenEditWarning(): string {
    return GLOBAL_TOKEN_EDIT_WARNINGS[this.method];
  }

  /**
   * This section's three settings as the tab's Save writes them: the sign mode, the Token / Salt
   * on screen and the global token edit switch. The Enabled switch is the tab's.
   */
  pendingSettings(): Record<string, any> {
    const keys = ACCESS_SETTINGS[this.method];
    return {
      [keys.signMode]: this.signMode,
      [keys.securityToken]: this.tokenToSave,
      [keys.globalTokenEdit]: this.globalTokenEdit
    };
  }

  onTokenInputChange(value: string): void {
    this.tokenInput = value || '';
    this.autoGenerated = false;
    this.emitState();
  }

  async generateToken(): Promise<void> {
    if (this.savedToken?.trim()) {
      const replace = await this.modal.confirm(
        `Replace the ${this.tokenWord}?`,
        `Once you save, every link made with the current ${this.tokenWord} stops working.`,
        'Replace'
      );
      if (!replace) return;
    }
    this.onTokenInputChange(generateSecurityToken());
  }

  onSignModeChange(mode: SignMode): void {
    this.signMode = mode;
    this.generateIfEmpty();
    this.emitState();
  }

  onIncludeExpiryChange(include: boolean): void {
    this.includeExpiry = !!include;
    this.expires = this.includeExpiry ? expiresInDays(30) : '';
    this.emitState();
  }

  private emitState(): void {
    this.stateChange.emit(this.state);
  }

  private initializeSettings(): void {
    if (!this.method) return;
    const settings = parseWidgetSettings(this.project?.widget_settings) || {};
    const access = accessOf(settings, this.method);
    const stored = settings[ACCESS_SETTINGS[this.method].securityToken];
    this.savedToken = typeof stored === 'string' ? stored : null;
    this.tokenInput = this.savedToken ?? '';
    this.signMode = access.signMode;
    this.globalTokenEdit = access.globalTokenEdit;
    const legacy = this.method === 'email' && legacySignAvailable(access);
    this.signModeOptions = SIGN_MODE_OPTIONS.filter((option) => option.value !== 'legacy_md5' || legacy);
    this.autoGenerated = false;
    // A field shown empty gets its first value straight away. The tab hears of it a moment later,
    // outside the change detection run that loaded the project.
    if (this.generateIfEmpty()) Promise.resolve().then(() => this.emitState());
  }

  /** Fills a shown, empty field with a fresh token / salt; nothing is replaced, so nobody is asked. */
  private generateIfEmpty(): boolean {
    if (!this.tokenVisible || this.tokenInput.trim()) return false;
    this.tokenInput = generateSecurityToken();
    this.autoGenerated = true;
    return true;
  }
}
